import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import NewProjectModalEnhanced from "./NewProjectModalEnhanced";
import { v4 as uuidv4 } from "uuid";
import { FiTrash2, FiCopy } from "react-icons/fi";
import { BsPencil } from "react-icons/bs";
import { IoIosAdd } from "react-icons/io";
import { FaFileImport, FaFileExport } from "react-icons/fa";
import ProjectJsonImporter from "./ProjectJsonImporter";
import { useCurrentTenant } from "../../hooks/useCurrentTenant";
import "./Projects.css";

const Projects = () => {
  const currentTenantId = useCurrentTenant();
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [scenarioList, setScenarioList] = useState([]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [enclosures, setEnclosures] = useState([]);
  const navigate = useNavigate();
  // Load all projects (exclude templates)
  const fetchProjects = async () => {
    try {
      const { data } = await api.get("/project");
      const list = Array.isArray(data) ? data : [];
      setProjects(list.filter(p => p?.isTemplate !== true));
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const tenantProjects = useMemo(
    () => projects.filter((p) => p.tenantId === currentTenantId),
    [projects, currentTenantId]
  );


  // Load all enclosures
  const fetchEnclosures = async () => {
    try {
      const res = await api.get("/enclosure");
      setEnclosures(res.data);
    } catch (err) {
      console.error("Failed to fetch Enclosures:", err);
    }
  };

  useEffect(() => {
    fetchEnclosures();
  }, []);

  const tenantEnclosures = useMemo(
    () => enclosures.filter((e) => e.tenantId === currentTenantId && e.ownership === "Tenant"),
    [enclosures, currentTenantId]
  );


  const enclosureMap = useMemo(() => {
    const map = {};
    tenantEnclosures.forEach(enc => {
      map[enc.id] = `${enc.manufacturer ?? "Unnamed"} - ${enc.name ?? "Unnamed"} (${enc.size.width} × ${enc.size.height} × ${enc.size.depth})`;
    });
    return map;
  }, [tenantEnclosures]);
  // -------------------------------------------------------------
  // Load scenario list
  // -------------------------------------------------------------
  useEffect(() => {
    api.get("/scenario")
      .then((res) => setScenarioList(res.data || []))
      .catch(() => { });
  }, []);

  const tenantScenarios = useMemo(
    () => scenarioList.filter((s) => s.tenantId === currentTenantId),
    [scenarioList, currentTenantId]
  );

  const scenarioMap = useMemo(() => {
    const map = {};
    tenantScenarios.forEach(sc => {
      map[sc.id] = sc.name;
    });
    return map;
  }, [tenantScenarios]);



  /* ---------- Selection ---------- */
  const toggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = rows => setSelectedIds(new Set(rows.map(p => p.id)));
  const clearSelection = () => setSelectedIds(new Set());

  /* ---------- Bulk Delete ---------- */
  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} project(s)?`)) return;

    try {
      for (const id of selectedIds) {
        await api.delete(`/project/${id}`);
      }
      await fetchProjects();
      clearSelection();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      alert("Some projects could not be deleted.");
    }
  };

  /* ---------- Filtering ---------- */
  const filteredProjects = useMemo(() => {
    const q = search.toLowerCase();
    return tenantProjects.filter(p =>
      p?.name?.toLowerCase().includes(q)
    );
  }, [tenantProjects, search]);

  const handleCreateProject = async ({ name, scenarioId }) => {
    try {
      // resetAllState();
      const projectId = uuidv4();

      const { data: project } = await api.post("/project", {
        id: projectId,
        tenantId: "",
        name,
        scenarioId
      });

      // ✅ Navigate to panel layout WITH project
      navigate(`/projects/${project.id}`)


    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm("Delete this project?")) return;

    try {
      await api.delete(`/project/${id}`);
      await fetchProjects();
      clearSelection();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("❌ Project could not be deleted.");
    }
  };

  const handleCopy = async id => {
    try {
      const { data: project } = await api.post(`/project/${id}/copy`);
      await fetchProjects();
      alert("✅ Project copied successfully.");
      // ✅ Navigate to panel layout WITH project
      navigate(`/projects/${project.id}`)
    } catch (err) {
      console.error("Copy failed:", err);
      alert("❌ Could not copy project.");
    }
  };

  const handleExportProject = async id => {
    try {
      const res = await api.get(`/project/${id}/export`);

      const blob = new Blob(
        [JSON.stringify(res.data, null, 2)],
        { type: "application/json" }
      );

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `Project_${id}.json`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed");
    }
  };

  return (
    <div className="project-table-container">
      {/* Header */}
      <h2>Projects</h2>
      {/* <input
          type="search"
          className="device-search-table-rt"
          placeholder="Search projects..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        /> */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "1rem",
        }}
      >
        <button className="btn-newproject"
          onClick={() => setShowNewProjectModal(true)}
        >
          <span><IoIosAdd /></span> New Project
        </button>

        <div className="project-actions">
          {/* IMPORT */}
          <button
            className="btn-importscenario"
            title="Import Project from JSON"
            onClick={() =>
              document.getElementById("project-json-importer").click()
            }
          >
            <FaFileImport /> Import
          </button>
          <button
            className="btn-delete-all"
            disabled={!selectedIds.size}
            onClick={handleBulkDelete}
          >
            <span><FiTrash2 /></span> Delete Selected ({selectedIds.size})
          </button>
        </div>
      </div>

      {/* Table */}
      <table className="section-table">
        <thead>
          <tr>
            <th className="checkbox-column">
              <input
                type="checkbox"
                checked={
                  filteredProjects.length > 0 &&
                  selectedIds.size === filteredProjects.length
                }
                onChange={e =>
                  e.target.checked
                    ? selectAll(filteredProjects)
                    : clearSelection()
                }
              />
            </th>
            <th>Name</th>
            <th>Scenario</th>
            <th>Enclosure</th>
            <th>Created</th>
            <th>Modified</th>
            <th className="actions-column">Actions</th>
          </tr>
        </thead>

        <tbody>
          {filteredProjects.map(p => (
            <tr key={p.id}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedIds.has(p.id)}
                  onChange={() => toggleSelect(p.id)}
                />
              </td>

              <td>{p.name}</td>
              <td>{scenarioMap[p.scenarioId] ?? "—"}</td>
              <td>{enclosureMap[p.enclosurePlacement?.referenceId] ?? "—"}</td>

              <td>
                {p.createdAt
                  ? new Date(p.createdAt).toLocaleDateString()
                  : "-"}
              </td>

              <td>
                {p.updatedAt
                  ? new Date(p.updatedAt).toLocaleDateString()
                  : "-"}
              </td>

              <td className="action-buttons">

                {/* Edit */}
                <button
                  className="btn-icon-projects"
                  title="Edit"
                  onClick={() => navigate(`/projects/${p.id}`)}

                >
                  <BsPencil />
                </button>


                {/* Delete */}
                <button
                  className="btn-icon-projects delete"
                  title="Delete"
                  onClick={() => handleDelete(p.id)}
                >
                  <FiTrash2 />
                </button>

                {/* Delete */}
                <button
                  className="btn-icon-projects copy"
                  title="Copy"
                  onClick={() => handleCopy(p.id)}
                >
                  <FiCopy />
                </button>

                <button
                  className="btn-icon-projects copy"
                  title="Export"
                  onClick={() => handleExportProject(p.id)}
                >
                  <FaFileExport />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* NEW PROJECT MODAL */}
      {showNewProjectModal && (
        <NewProjectModalEnhanced
          scenarios={tenantScenarios}
          onCreate={handleCreateProject}
          onClose={() => setShowNewProjectModal(false)}
        />
      )}

      {/* Hidden input */}

      <ProjectJsonImporter
        inputId="project-json-importer"
        onImportComplete={async () => {
          await fetchProjects();   // ✅ reload list
        }}
      />
    </div>
  );
};

export default Projects;